import * as React from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Accordion, Loader } from 'semantic-ui-react';

import { cleanRecordSetSummary, getRecordsSetSummary } from '../../../actions/AlphasActions';
import { getRecordsSetSummarySimulation } from '../../../actions/Simulation/DataAction';
import { localeData } from '../../../locales/data';
import { StateType } from '../../../reducers';
import {
  activeTabIndexSelector,
  getTabsSelector,
  isTestPeriodControlShownSelector,
  isTestPeriodShownSelector,
} from '../../../reducers/EditorReducer/EditorSelector';
import { DispatchConnect } from '../../../types';

import { AlphaSummaryStateDispatchers, SummaryMainGeneric, SummaryMainProps, SummaryStateToProps } from './types';
import './Summary.scss';
import SummaryDataTable from './SummaryDataTable';
import { SummaryLine } from './SummaryStatus';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import AlphaRiskNeutralizedSummary from '../AlphaRiskNeutralizedSummary';
import AlphaInvestabilityConstrainedSummary from '../AlphaInvestabilityConstrainedSummary';
import AlphaRegionSummaryIntl from '../AlphaRegionSummary';

class SummaryMain extends React.Component<SummaryMainGeneric> {
  getRecordSet = (recordOption: string, id: any) => {
    if (this.props.isSimulation) {
      const indexTab = this.props.currentTabActive;
      const tabId = this.props.editorTabs.getIn([indexTab, 'id']);
      const childIndex = this.props.editorTabs.getIn([indexTab, 'activeChildTab']);
      const idChild = this.props.editorTabs.getIn([indexTab, 'childTabs', childIndex, 'id']);
      this.props.dispatchGetRecordsSetSummarySimulation(recordOption, id, tabId, idChild ?? null);
    } else {
      this.props.dispatchGetRecordsSetSummary(recordOption, id);
    }
  };

  componentDidMount() {
    const alphaId = this.props.alpha && this.props.alpha.get('id');
    if (this.props.alpha) {
      this.getRecordSet('yearly-stats', alphaId);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: SummaryMainGeneric) {
    if (this.props.alfaId !== nextProps.alfaId) {
      this.props.dispatchCleanRecordsSetSummary();
      this.getRecordSet('yearly-stats', nextProps.alfaId);
    }
  }

  componentDidUpdate(prevProps: SummaryMainGeneric) {
    if (this.props.isSimulation && prevProps.alfaId !== this.props.alfaId) {
      this.getRecordSet('yearly-stats', this.props.alfaId);
    }
  }

  handlerMetrics = () => this.props.handleClick('aggregateData');

  regions = ['amer', 'apac', 'emea'];

  render() {
    const {
      tableHeader,
      tableInfo,
      typeCheck,
      typeStage,
      summaryTable,
      summaryTableOSPROD,
      isFetchingRecordSetSummary,
      alpha,
      isTestPeriodShown,
      isTestPeriodControlShown,
    } = this.props;
    if (isFetchingRecordSetSummary) {
      return (
        <div className="loader">
          <Loader active={true} inline="centered" size="massive" />
        </div>
      );
    } else if (tableInfo !== null && tableHeader !== null) {
      const noSummaryMessage = alpha?.get('author') ? 'summary.noYearlyResults.alphas' : 'summary.noYearlyResults';
      return (
        <div>
          {
            <Accordion>
              <Accordion.Title active={typeCheck}>
                <SummaryLine
                  tableHeader={tableHeader.summary}
                  tableInfo={tableInfo}
                  typeCheck={typeCheck}
                  handlerMetrics={this.handlerMetrics}
                  typeStage={typeStage}
                  showAccordionArrow={summaryTable !== null}
                />
                {summaryTable == null && (
                  <div className="summary__no-yearly-results">
                    <FormattedMessageHelper id={noSummaryMessage} />
                  </div>
                )}
              </Accordion.Title>
              <Accordion.Content active={typeCheck}>
                {summaryTable !== null && (
                  <SummaryDataTable
                    isTestPeriodControlShown={isTestPeriodControlShown}
                    isTestPeriodShown={isTestPeriodShown}
                    summaryTable={summaryTable}
                    summaryTableOSPROD={summaryTableOSPROD}
                    typeStage={typeStage}
                  />
                )}
              </Accordion.Content>
            </Accordion>
          }
          {this.regions.map(region => {
            return <AlphaRegionSummaryIntl key={region} typeStage={typeStage} region={region} alpha={alpha} />;
          })}
          <AlphaRiskNeutralizedSummary alpha={alpha} typeStage={typeStage} />
          <AlphaInvestabilityConstrainedSummary alpha={alpha} typeStage={typeStage} />
        </div>
      );
    } else {
      return (
        <div className="summary__no-results">
          <FormattedMessageHelper
            tagName="span"
            id={'summary.noResults.alphas'}
            defaultMessage={localeData.en['summary.noResults.alphas']}
          />
        </div>
      );
    }
  }
}

function mapStateToProps(state: StateType, props: SummaryMainProps): SummaryStateToProps {
  const summaryTable = state.getIn(props.path.concat(['summaryTable']));
  const summaryTableOSPROD = state.getIn(props.path.concat(['summaryOSPROD']));
  const isFetchingRecordSetSummary = props.path.concat(['fetchingRecordSetsSummary']);
  const isFetchingRecordSet = props.path.concat(['fetchingRecordSets']);
  const alphaPath = props.path.concat(['alphaDetail']);

  return {
    currentTabActive: activeTabIndexSelector(state),
    editorTabs: getTabsSelector(state),
    summaryTable,
    summaryTableOSPROD,
    // @ts-ignore
    isFetchingRecordSetSummary: state.getIn(isFetchingRecordSetSummary) || state.getIn(isFetchingRecordSet),
    alpha: state.getIn(alphaPath),
    isTestPeriodShown: isTestPeriodShownSelector(state, props),
    isTestPeriodControlShown: isTestPeriodControlShownSelector(state, props),
  };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): AlphaSummaryStateDispatchers => ({
  dispatchGetRecordsSetSummary: async (recordOption: string, alphaId: string) => dispatch(getRecordsSetSummary(alphaId, recordOption)),
  dispatchGetRecordsSetSummarySimulation: async (recordOption: string, alphaId: string, idParent: string, idChild: string | null) =>
    dispatch(getRecordsSetSummarySimulation(alphaId, recordOption, idParent, idChild)),
  dispatchCleanRecordsSetSummary: () => dispatch(cleanRecordSetSummary()),
});

export default connect<SummaryStateToProps, DispatchConnect<AlphaSummaryStateDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(SummaryMain);
